import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { bindAllShippingStateSelects } from '@theme/shipping-state-select';

/**
 * Bottle threshold indicator for the top navigation bar.
 * Displays wine bottle icons that fill as wine products are added to cart.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} bottle - Array of bottle icon elements.
 *
 * @extends {Component<Refs>}
 */
class TopNavBottles extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.threshold = parseInt(this.dataset.threshold, 10) || 6;
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate);
  }

  /** @param {CustomEvent} event */
  #onCartUpdate = async (event) => {
    const cart = event.detail?.resource;
    if (cart?.items) {
      this.#countAndUpdate(cart);
    } else {
      await this.#fetchCart();
    }
  };

  async #fetchCart() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      this.#countAndUpdate(cart);
    } catch (e) { /* silent */ }
  }

  /** @param {{ items: Array<{ product_type?: string, quantity?: number, grams?: number }> }} cart */
  #countAndUpdate(cart) {
    const gramsPerPound = 453.59237;
    const lbsPerBottle = 4;
    // Absorbs Shopify's whole-gram rounding so a 4 lb bottle doesn't read as 0.
    const tolerance = 0.01;

    const wineLbs = cart.items.reduce((sum, item) => {
      const productType = (item.product_type || '').trim().toLowerCase();
      if (productType !== 'wine') return sum;

      const itemLbs = (Number(item.grams) || 0) / gramsPerPound;
      const quantity = Number(item.quantity) || 0;
      return sum + (itemLbs * quantity);
    }, 0);

    const wineCount = Math.floor((wineLbs + tolerance) / lbsPerBottle);
    const threshold = Number(this.threshold) || 6;
    this.#updateBottles(Math.min(wineCount, threshold));
  }

  /** @param {number} count */
  #updateBottles(count) {
    this.dataset.wineCount = String(count);
    if (!this.refs.bottle) return;

    this.refs.bottle.forEach((el) => {
      const index = parseInt(el.dataset.index, 10);
      el.classList.toggle('top-nav__bottle--empty', index > count);
    });
  }
}

if (!customElements.get('top-nav-bottles')) {
  customElements.define('top-nav-bottles', TopNavBottles);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bindAllShippingStateSelects);
} else {
  bindAllShippingStateSelects();
}

document.addEventListener('shopify:section:load', bindAllShippingStateSelects);
